import * as React from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BackLink,
  Button,
  Modal,
  NavRail,
  PageHeader,
  Textarea,
} from "@ardept/design-system";
import { useEmailDraft, useMe } from "../api/queries.js";
import { railVendorName } from "../auth/railVendorName.js";
import { usePostEmailDraftDecision } from "../api/usePostEmailDraftDecision.js";
import "./EmailDraftDetailScreen.css";
import "./screens.css";

// Statuses where operator can still take action
const ACTIONABLE_STATUSES = ["pending", "guardrail_passed", "guardrail_failed"];

type ModalMode = null | "approve" | "reject";

export function EmailDraftDetailScreen(): JSX.Element {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();

  const { data: draft, isLoading, error } = useEmailDraft(draftId ?? "");
  const mutation = usePostEmailDraftDecision(draftId ?? "");
  const { data: me } = useMe();

  const [mode, setMode] = useState<ModalMode>(null);
  const [rationale, setRationale] = useState("");

  // Build operator display for NavRail from /me response
  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  function closeModal() {
    setMode(null);
    setRationale("");
  }

  function submitApprove() {
    mutation.mutate(
      { decision: "approve" },
      {
        onSuccess: () => {
          closeModal();
          navigate("/email-drafts");
        },
      },
    );
  }

  function submitReject() {
    if (!rationale.trim()) return;
    mutation.mutate(
      { decision: "reject", rationale: rationale.trim() },
      {
        onSuccess: () => {
          closeModal();
          navigate("/email-drafts");
        },
      },
    );
  }

  if (isLoading && !draft) {
    return (
      <div className="app">
        <NavRail active="drafts" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
        <main className="main">
          <div className="detail-loading">Loading email draft...</div>
        </main>
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="app">
        <NavRail active="drafts" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
        <main className="main">
          <BackLink to="/email-drafts" navigate={navigate} label="Back to email drafts" />
          <div className="detail-error">
            Email draft not found, or you do not have access. Check the URL or your tenant scope.
          </div>
        </main>
      </div>
    );
  }

  const canDecide = ACTIONABLE_STATUSES.includes(draft.status);
  const titleDisplay =
    draft.subject.length > 100
      ? draft.subject.slice(0, 100) + "..."
      : draft.subject;

  return (
    <div className="app">
      <NavRail active="drafts" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
      <main className="main">
        <BackLink to="/email-drafts" navigate={navigate} label="Back to email drafts" />
        <PageHeader title={titleDisplay} />

        {/* Draft metadata */}
        <dl className="draft-meta">
          <dt>Tenant</dt>
          <dd className="mono">{draft.tenant_slug}</dd>
          <dt>Job</dt>
          <dd>
            <button
              className="link-cell"
              onClick={() => navigate(`/jobs/${draft.job_id}`)}
            >
              {draft.job_id}
            </button>
          </dd>
          <dt>Recipient</dt>
          <dd>
            {draft.recipient_name
              ? `${draft.recipient_name} <${draft.recipient_email}>`
              : draft.recipient_email}
          </dd>
          <dt>Message type</dt>
          <dd>{draft.message_type}</dd>
          <dt>Status</dt>
          <dd>
            <span className={`status-badge status-${draft.status}`}>{draft.status}</span>
          </dd>
          <dt>Created</dt>
          <dd>{new Date(draft.created_at).toLocaleString()}</dd>
          {draft.guardrail_reasons.length > 0 ? (
            <>
              <dt>Guardrail reasons</dt>
              <dd>
                <ul className="guardrail-reasons">
                  {draft.guardrail_reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </dd>
            </>
          ) : null}
        </dl>

        {/* Body section - text only for XSS safety (D-DRAFT discretion) */}
        <section className="body-section">
          <h2 className="ard-h2 body-section__title">Body</h2>
          {/* T-17-09-01 mitigation: render body_text as text node, not innerHTML.
              HTML body sanitization deferred to v2 (Sprint 5+) with DOMPurify. */}
          <pre className="body-text">{draft.body_text}</pre>
          {draft.body_html ? (
            <p className="html-body-note">
              <em>HTML body present; text shown for security. Rich render deferred to v2.</em>
            </p>
          ) : null}
        </section>

        {/* Error banner */}
        {mutation.isError ? (
          <div className="error-band">
            {(mutation.error as { status?: number })?.status === undefined ||
            (mutation.error as { status?: number })?.status! >= 500
              ? "Could not reach the server. Try again."
              : "Could not record this decision. Check the draft status and try again."}
          </div>
        ) : null}

        {/* Approve/reject actions — only for actionable statuses */}
        {canDecide ? (
          <div className="draft-actions">
            <Button variant="ghost" onClick={() => setMode("reject")}>
              Reject draft
            </Button>
            <Button variant="primary" onClick={() => setMode("approve")}>
              Approve draft
            </Button>
          </div>
        ) : null}

        {/* Approve modal */}
        <Modal
          open={mode === "approve"}
          onClose={closeModal}
          title="Approve and send?"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={closeModal}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitApprove}
                disabled={mutation.isPending}
              >
                {mutation.isPending && mode === "approve"
                  ? "Sending..."
                  : "Send"}
              </Button>
            </>
          }
        >
          <p className="modal__sub" style={{ marginBottom: 14 }}>
            Approving will send this email immediately via the configured ACS sender.
          </p>
        </Modal>

        {/* Reject modal — rationale required */}
        <Modal
          open={mode === "reject"}
          onClose={closeModal}
          title="Reject draft?"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={closeModal}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={submitReject}
                disabled={!rationale.trim() || mutation.isPending}
              >
                {mutation.isPending && mode === "reject"
                  ? "Rejecting..."
                  : "Reject"}
              </Button>
            </>
          }
        >
          <p className="modal__sub" style={{ marginBottom: 14 }}>
            The draft will not be sent. The rationale is written to the audit log.
          </p>
          <label>
            Rationale <span className="req">*</span>
          </label>
          <Textarea
            value={rationale}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setRationale(e.target.value)
            }
            placeholder="Reason for rejection (required)"
            rows={4}
            required
          />
          {mutation.isError && mode === "reject" ? (
            <div className="error-band">
              Could not record this decision. Check the draft status and try again.
            </div>
          ) : null}
        </Modal>
      </main>
    </div>
  );
}
